import * as React from "react";
import LoadingOutlined from "@ant-design/icons/LoadingOutlined";
import PaperClipOutlined from "@ant-design/icons/PaperClipOutlined";
import PictureTwoTone from "@ant-design/icons/PictureTwoTone";
import FileTwoTone from "@ant-design/icons/FileTwoTone";
import EyeOutlined from "@ant-design/icons/EyeOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import DownloadOutlined from "@ant-design/icons/DownloadOutlined";
import cx from "classnames";
import Motion, { CSSMotionList as MotionList } from "rc-motion";
import collapseMotion from "antd/es/_util/motion";
import { cloneElement, isValidElement } from "antd/es/_util/reactNode";
import { previewImage, isImageUrl } from "antd/es/upload/utils";
import { ConfigContext } from "antd/es/config-provider";
import useForceUpdate from "antd/es/_util/hooks/useForceUpdate";
import Button from "../button";
import Progress from "../progress";
import Tooltip from "../tooltip";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const listItemMotion = {
  ...collapseMotion
};
delete listItemMotion.onAppearEnd;
delete listItemMotion.onEnterEnd;
delete listItemMotion.onLeaveEnd;
const ListItem = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    prefixCls,
    className,
    style,
    locale,
    listType,
    file,
    items,
    iconRender,
    actionIconRender,
    itemRender,
    isImgUrl,
    showPreviewIcon,
    showRemoveIcon,
    showDownloadIcon,
    previewIcon: customPreviewIcon,
    removeIcon: customRemoveIcon,
    downloadIcon: customDownloadIcon,
    onPreview,
    onDownload,
    onClose
  } = _ref;
  const [showProgress, setShowProgress] = React.useState(false);
  const progressRafRef = React.useRef();
  React.useEffect(() => {
    progressRafRef.current = setTimeout(() => {
      setShowProgress(true);
    }, 300);
    return () => {
      window.clearTimeout(progressRafRef.current);
    };
  }, []);

  // This is used for legacy span make scrollHeight the wrong value.
  // We will force these to be `display: block` with non `picture-card`
  const spanClassName = `${prefixCls}-span`;
  const iconNode = iconRender(file);
  let icon = /*#__PURE__*/_jsx("div", {
    className: `${prefixCls}-text-icon`,
    children: iconNode
  });
  if (listType === "picture" || listType === "picture-card") {
    if (file.status === "uploading" || !file.thumbUrl && !file.url) {
      const uploadingClassName = cx({
        [`${prefixCls}-list-item-thumbnail`]: true,
        [`${prefixCls}-list-item-file`]: file.status !== "uploading"
      });
      icon = /*#__PURE__*/_jsx("div", {
        className: uploadingClassName,
        children: iconNode
      });
    } else {
      const thumbnail = isImgUrl?.(file) ? /*#__PURE__*/_jsx("img", {
        src: file.thumbUrl || file.url,
        alt: file.name,
        className: `${prefixCls}-list-item-image`,
        crossOrigin: file.crossOrigin
      }) : iconNode;
      const aClassName = cx({
        [`${prefixCls}-list-item-thumbnail`]: true,
        [`${prefixCls}-list-item-file`]: isImgUrl && !isImgUrl(file)
      });
      icon = /*#__PURE__*/_jsx("a", {
        className: aClassName,
        onClick: e => onPreview(file, e),
        href: file.url || file.thumbUrl,
        target: "_blank",
        rel: "noopener noreferrer",
        children: thumbnail
      });
    }
  }
  const infoUploadingClass = cx({
    [`${prefixCls}-list-item`]: true,
    [`${prefixCls}-list-item-${file.status}`]: true,
    [`${prefixCls}-list-item-list-type-${listType}`]: true
  });
  const linkProps = typeof file.linkProps === "string" ? JSON.parse(file.linkProps) : file.linkProps;
  const removeIcon = showRemoveIcon ? actionIconRender((typeof customRemoveIcon === "function" ? customRemoveIcon(file) : customRemoveIcon) || /*#__PURE__*/_jsx(DeleteOutlined, {}), () => onClose(file), prefixCls, locale.removeFile) : null;
  const downloadIcon = showDownloadIcon && file.status === "done" ? actionIconRender((typeof customDownloadIcon === "function" ? customDownloadIcon(file) : customDownloadIcon) || /*#__PURE__*/_jsx(DownloadOutlined, {}), () => onDownload(file), prefixCls, locale.downloadFile) : null;
  const downloadOrDelete = listType !== "picture-card" && /*#__PURE__*/_jsxs("span", {
    className: cx(`${prefixCls}-list-item-card-actions`, {
      picture: listType === "picture"
    }),
    children: [downloadIcon, removeIcon]
  }, "download-delete");
  const listItemNameClass = cx(`${prefixCls}-list-item-name`);
  const preview = file.url ? [/*#__PURE__*/_jsx("a", {
    target: "_blank",
    rel: "noopener noreferrer",
    className: listItemNameClass,
    title: file.name,
    ...linkProps,
    href: file.url,
    onClick: e => onPreview(file, e),
    children: file.name
  }, "view"), downloadOrDelete] : [/*#__PURE__*/_jsx("span", {
    className: listItemNameClass,
    onClick: e => onPreview(file, e),
    title: file.name,
    children: file.name
  }, "view"), downloadOrDelete];
  const previewStyle = {
    pointerEvents: "none",
    opacity: 0.5
  };
  const previewIcon = showPreviewIcon ? /*#__PURE__*/_jsx("a", {
    href: file.url || file.thumbUrl,
    target: "_blank",
    rel: "noopener noreferrer",
    style: file.url || file.thumbUrl ? undefined : previewStyle,
    onClick: e => onPreview(file, e),
    title: locale.previewFile,
    children: typeof customPreviewIcon === "function" ? customPreviewIcon(file) : customPreviewIcon || /*#__PURE__*/_jsx(EyeOutlined, {})
  }) : null;
  const actions = listType === "picture-card" && file.status !== "uploading" && /*#__PURE__*/_jsxs("span", {
    className: `${prefixCls}-list-item-actions`,
    children: [previewIcon, file.status === "done" && downloadIcon, removeIcon]
  });
  let message;
  if (file.response && typeof file.response === "string") {
    message = file.response;
  } else {
    message = file.error?.statusText || file.error?.message || locale.uploadError;
  }
  const iconAndPreview = /*#__PURE__*/_jsxs("span", {
    className: spanClassName,
    children: [icon, preview]
  });
  const {
    getPrefixCls
  } = React.useContext(ConfigContext);
  const rootPrefixCls = getPrefixCls();
  const dom = /*#__PURE__*/_jsxs("div", {
    className: infoUploadingClass,
    children: [/*#__PURE__*/_jsx("div", {
      className: `${prefixCls}-list-item-info`,
      children: iconAndPreview
    }), actions, showProgress && /*#__PURE__*/_jsx(Motion, {
      motionName: `${rootPrefixCls}-fade`,
      visible: file.status === "uploading",
      motionDeadline: 2000,
      children: _ref2 => {
        let {
          className: motionClassName
        } = _ref2;
        // show loading icon if upload progress listener is disabled
        const loadingProgress = "percent" in file ? /*#__PURE__*/_jsx(Progress, {
          value: file.percent,
          showInfo: false
        }) : null;
        return /*#__PURE__*/_jsx("div", {
          className: cx(`${prefixCls}-list-item-progress`, motionClassName),
          children: loadingProgress
        });
      }
    })]
  });
  const listContainerNameClass = cx(`${prefixCls}-list-${listType}-container`, className);
  const item = file.status === "error" ? /*#__PURE__*/_jsx(Tooltip, {
    title: message,
    getPopupContainer: node => node.parentNode,
    children: dom
  }) : dom;
  return /*#__PURE__*/_jsx("div", {
    className: listContainerNameClass,
    style: style,
    ref: ref,
    children: itemRender ? itemRender(item, file, items, {
      download: onDownload.bind(null, file),
      preview: onPreview.bind(null, file),
      remove: onClose.bind(null, file)
    }) : item
  });
});
const InternalUploadList = (_ref3, ref) => {
  let {
    listType,
    previewFile,
    onPreview,
    onDownload,
    onRemove,
    locale,
    iconRender,
    isImageUrl: isImgUrl,
    prefixCls: customizePrefixCls,
    items = [],
    showPreviewIcon,
    showRemoveIcon,
    showDownloadIcon,
    removeIcon,
    previewIcon,
    downloadIcon,
    appendAction,
    appendActionVisible,
    itemRender
  } = _ref3;
  const forceUpdate = useForceUpdate();
  const [motionAppear, setMotionAppear] = React.useState(false);

  // ============================= Effect =============================
  React.useEffect(() => {
    if (listType !== "picture" && listType !== "picture-card") {
      return;
    }
    (items || []).forEach(file => {
      if (typeof document === "undefined" || typeof window === "undefined" || !window.FileReader || !window.File || !(file.originFileObj instanceof File || file.originFileObj instanceof Blob) || file.thumbUrl !== undefined) {
        return;
      }
      file.thumbUrl = "";
      if (previewFile) {
        previewFile(file.originFileObj).then(previewDataUrl => {
          // Need append '' to avoid dead loop
          file.thumbUrl = previewDataUrl || "";
          forceUpdate();
        });
      }
    });
  }, [listType, items, previewFile]);
  React.useEffect(() => {
    setMotionAppear(true);
  }, []);

  // ============================= Events =============================
  const onInternalPreview = (file, e) => {
    if (!onPreview) {
      return;
    }
    e?.preventDefault();
    return onPreview(file);
  };
  const onInternalDownload = file => {
    if (typeof onDownload === "function") {
      onDownload(file);
    } else if (file.url) {
      window.open(file.url);
    }
  };
  const onInternalClose = file => {
    onRemove?.(file);
  };
  const internalIconRender = file => {
    if (iconRender) {
      return iconRender(file, listType);
    }
    const isLoading = file.status === "uploading";
    const fileIcon = isImgUrl && isImgUrl(file) ? /*#__PURE__*/_jsx(PictureTwoTone, {}) : /*#__PURE__*/_jsx(FileTwoTone, {});
    let icon = isLoading ? /*#__PURE__*/_jsx(LoadingOutlined, {}) : /*#__PURE__*/_jsx(PaperClipOutlined, {});
    if (listType === "picture") {
      icon = isLoading ? /*#__PURE__*/_jsx(LoadingOutlined, {}) : fileIcon;
    } else if (listType === "picture-card") {
      icon = isLoading ? locale.uploading : fileIcon;
    }
    return icon;
  };
  const actionIconRender = (customIcon, callback, prefixCls, title) => {
    const btnProps = {
      type: "text",
      size: "small",
      title,
      onClick: e => {
        callback();
        if (isValidElement(customIcon) && customIcon.props.onClick) {
          customIcon.props.onClick(e);
        }
      },
      className: `${prefixCls}-list-item-card-actions-btn`
    };
    if (isValidElement(customIcon)) {
      const btnIcon = cloneElement(customIcon, {
        ...customIcon.props,
        onClick: () => {}
      });
      return /*#__PURE__*/_jsx(Button, {
        ...btnProps,
        icon: btnIcon
      });
    }
    return /*#__PURE__*/_jsx(Button, {
      ...btnProps,
      children: /*#__PURE__*/_jsx("span", {
        children: customIcon
      })
    });
  };

  // ============================== Ref ===============================
  React.useImperativeHandle(ref, () => ({
    handlePreview: onInternalPreview,
    handleDownload: onInternalDownload
  }));
  const {
    getPrefixCls,
    direction
  } = React.useContext(ConfigContext);

  // ============================= Render =============================
  const prefixCls = getPrefixCls("upload", customizePrefixCls);
  const listClassNames = cx({
    [`${prefixCls}-list`]: true,
    [`${prefixCls}-list-${listType}`]: true,
    [`${prefixCls}-list-rtl`]: direction === "rtl"
  });

  // Motion config
  const motionKeyList = [...items.map(file => ({
    key: file.uid,
    file
  }))];
  const animationDirection = listType === "picture-card" ? "animate-inline" : "animate";
  // const transitionName = list.length === 0 ? '' : `${prefixCls}-${animationDirection}`;

  let motionConfig = {
    motionDeadline: 2000,
    motionName: `${prefixCls}-${animationDirection}`,
    keys: motionKeyList,
    motionAppear
  };
  if (listType !== "picture-card") {
    motionConfig = {
      ...listItemMotion,
      ...motionConfig
    };
  }
  return /*#__PURE__*/_jsxs("div", {
    className: listClassNames,
    children: [/*#__PURE__*/_jsx(MotionList, {
      ...motionConfig,
      component: false,
      children: _ref4 => {
        let {
          key,
          file,
          className: motionClassName,
          style: motionStyle
        } = _ref4;
        return /*#__PURE__*/_jsx(ListItem, {
          locale: locale,
          prefixCls: prefixCls,
          className: motionClassName,
          style: motionStyle,
          file: file,
          items: items,
          listType: listType,
          isImgUrl: isImgUrl,
          showPreviewIcon: showPreviewIcon,
          showRemoveIcon: showRemoveIcon,
          showDownloadIcon: showDownloadIcon,
          removeIcon: removeIcon,
          previewIcon: previewIcon,
          downloadIcon: downloadIcon,
          iconRender: internalIconRender,
          actionIconRender: actionIconRender,
          itemRender: itemRender,
          onPreview: onInternalPreview,
          onDownload: onInternalDownload,
          onClose: onInternalClose
        }, key);
      }
    }), appendAction && /*#__PURE__*/_jsx(Motion, {
      ...motionConfig,
      visible: appendActionVisible,
      forceRender: true,
      children: _ref5 => {
        let {
          className: motionClassName,
          style: motionStyle
        } = _ref5;
        return cloneElement(appendAction, oriProps => ({
          className: cx(oriProps.className, motionClassName),
          style: {
            ...motionStyle,
            // prevent the element has hover css pseudo-class that may cause animation to end prematurely.
            pointerEvents: motionClassName ? "none" : undefined,
            ...oriProps.style
          }
        }));
      }
    })]
  });
};
const UploadList = /*#__PURE__*/React.forwardRef(InternalUploadList);
UploadList.displayName = "UploadList";
UploadList.defaultProps = {
  listType: "text",
  // or picture
  showRemoveIcon: true,
  showDownloadIcon: false,
  showPreviewIcon: true,
  appendActionVisible: true,
  previewFile: previewImage,
  isImageUrl
};
export default UploadList;